import * as THREE from 'three';

// ============================================================
// Collision detection — raycasts forward to detect buildings/terrain
// ============================================================

const COLLISION_DISTANCE = 12;
const RAY_LOOKAHEAD = 25;
const GROUND_LEVEL = 2;

const _forward = new THREE.Vector3();
const _origin = new THREE.Vector3();
const _raycaster = new THREE.Raycaster();

const _losOrigin = new THREE.Vector3();
const _losDir = new THREE.Vector3();
const _losRay = new THREE.Raycaster();

export class CollisionSystem {
  private readonly collidables: THREE.Object3D[] = [];

  addCollidable(object: THREE.Object3D): void {
    this.collidables.push(object);
  }

  /** Returns null if no collision, or a string describing what was hit */
  check(aircraft: THREE.Object3D): string | null {
    if (aircraft.position.y <= GROUND_LEVEL) {
      return 'the water';
    }

    _origin.copy(aircraft.position);
    _forward.set(0, 0, -1).applyQuaternion(aircraft.quaternion);
    _raycaster.set(_origin, _forward);
    _raycaster.far = RAY_LOOKAHEAD;

    const hits = _raycaster.intersectObjects(this.collidables, true);
    if (hits.length > 0 && hits[0].distance < COLLISION_DISTANCE) {
      // Walk up to find the named parent
      let obj: THREE.Object3D | null = hits[0].object;
      while (obj) {
        if (obj.name === 'target-ship') return 'the ship';
        if (obj.name === 'carrier') return 'the aircraft carrier';
        if (obj.name.startsWith('chunk_')) return 'a building';
        obj = obj.parent;
      }
      return 'a building';
    }

    return null;
  }

  /** Returns true if there is clear line of sight between two points */
  hasLineOfSight(from: THREE.Vector3, to: THREE.Vector3): boolean {
    _losDir.copy(to).sub(from);
    const dist = _losDir.length();
    if (dist < 0.001) return true;
    _losDir.normalize();

    _losRay.set(from, _losDir);
    _losRay.far = dist;

    const hits = _losRay.intersectObjects(this.collidables, true);
    return hits.length === 0;
  }

  /** Check if a projectile at `pos` moving in `dir` hits a building within `range`. Returns true if blocked. */
  isBlockedByBuilding(pos: THREE.Vector3, dir: THREE.Vector3, range: number): boolean {
    _losRay.set(pos, dir);
    _losRay.far = range;
    const hits = _losRay.intersectObjects(this.collidables, true);
    return hits.length > 0;
  }
}
